import moment from 'moment';
import { isAfter, isBefore, isSameDay, parse, parseISO } from 'date-fns';
import { DateUtils } from 'react-day-picker';
import { utcToZonedTime, zonedTimeToUtc, toDate } from 'date-fns-tz';

export const Format = 'MM/DD/YYYY HH:mm:ss';
export const ISO_Format = 'YYYY-MM-DD HH:mm:ssZ';
export const ISO_Format_Date = 'YYYY-MM-DD';
export const ISO_Format_Date2 = 'yyyy-MM-dd';
export const DATE_FORMAT = 'MM/dd/yyyy';

export const CSTTimeZone = 'America/Chicago';

/**
 * Helper that builds a time stamp out of Date and a Time with `YYYY-MM-DD HH:mm:ssZ` format
 * @param date a Date which can be either a Date or an String
 * @param time an String with HH:mm format
 * @returns a Date object according to the `Date` and `Time` supplied
 */
export const buildTimeStamp = (date: Date | string, time: string): Date => {
  const [hh, mm] = time.split(':').map((e) => parseInt(e));
  const copy = new Date(date);
  copy.setHours(hh);
  copy.setMinutes(mm);
  return copy;
};

/**
 * Formats a Date using `YYYY-MM-DD` by default, or any other format
 * @param date a Date which can be either a Date or an String
 * @param format a valid formatting format for dates
 * @returns an Date formatted using a supplied format or `YYYY-MM-DD` if not.
 */
const formatDate = (date: string | Date, format = ISO_Format_Date): string =>
  moment(date).format(format);

/**
 * Parses a `date` string using a `format`
 * @param str a Date string, like for instance `11/24/2021`
 * @param format a format which matches the given date format
 * @returns a Date object if both, the given date and the format were correct
 * or undefined if provided date has wrong format
 */
export const parseDate = (str: string, format: string): Date | undefined => {
  const parsed = parse(str, format, new Date());
  if (DateUtils.isDate(parsed)) {
    return parsed;
  }
  return undefined;
};

/**
 * Validates that a given `startDate` is earlier or equal to `endDate`.
 * Both dates have to the supplied using the `MM/dd/yyyy` format
 * @param startDate
 * @param endDate
 * @returns true if `startDate` is earlier/before or equal than `endDate`
 */
export const validateDates = <T extends Date | string>(
  startDate: T,
  endDate: T,
): boolean => {
  if (startDate instanceof Date && endDate instanceof Date) {
    return isBefore(startDate, endDate) || isSameDay(startDate, endDate);
  }
  const parsedStartDate = parseDate(startDate.toString(), DATE_FORMAT);
  const parsedEndDate = parseDate(endDate.toString(), DATE_FORMAT);
  if (parsedStartDate === undefined || parsedEndDate === undefined) {
    return false;
  }
  return (
    isBefore(parsedStartDate, parsedEndDate) ||
    isSameDay(parsedStartDate, parsedEndDate)
  );
};

/**
 * Test if a given date is after another date in comparison
 * @param givenDate given date
 * @returns true is given date is after in comparison with the other date
 */
export const dateIsAfterCurrentDate = (givenDate: string | Date): boolean => {
  if (givenDate instanceof Date) {
    return isAfter(givenDate, new Date());
  }
  return isAfter(new Date(givenDate), new Date());
};

/**
 * Checks whether or not start date is earlier or equal to end date
 * @param startDate Start Date
 * @param endDate End Date
 * @returns true if `startDate` is earlier/before or equal than `endDate`
 */
export const checkDates = (
  startDate?: Date,
  endDate?: Date | null,
): boolean => {
  if (startDate instanceof Date && endDate instanceof Date) {
    return validateDates(startDate, endDate);
  }
  return false;
};

/**
 * 00:00hs in local timezone
 * @returns a date representing 00:00hs
 */
export const midnight = (): Date => {
  const midnight = toDate(new Date(), { timeZone: CSTTimeZone });
  midnight.setHours(0);
  midnight.setMinutes(0);
  return midnight;
};

/**
 * 23:59hs in local timezone
 * @returns a date representing 23:59hs
 */
export const oneMinuteToMidnight = (): Date => {
  const oneMinuteToMidnight = toDate(new Date(), {
    timeZone: CSTTimeZone,
  });
  oneMinuteToMidnight.setHours(23);
  oneMinuteToMidnight.setMinutes(59);
  return oneMinuteToMidnight;
};

/**
 * Returns a Date object with Time set according to Hours and Minutes provided by _time_ arg
 * @param date a Date object
 * @param time An string with format 'HH:mm'
 * @returns Returns a Date object with Time set according to Hours and Minutes provided
 */
export const getCurrentCSTDateWithTime = (date: Date, time: string): Date => {
  const [hh, mm] = time.split(':').map((e) => parseInt(e));
  date.setHours(hh);
  date.setMinutes(mm);
  return zonedTimeToUtc(date, CSTTimeZone);
};

export const utcToCST = (dateStr: string): Date =>
  utcToZonedTime(parseISO(dateStr), CSTTimeZone);

/**
 * Converts a CST timezone date into a UTC timezone
 * @param date the CST Date
 * @returns an ISO formatted string representation of the Date
 */
export const cstToUTC = (date: Date): string =>
  zonedTimeToUtc(date, CSTTimeZone).toISOString();

export { formatDate };
